import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import customerService from "@plannel/services/data/customer-service";
import customerHrchyService from "@plannel/services/data/customer-hrchy-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import AttributeConfigModal from "@plannel/components/modal/AttributeConfigModal";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, FilterButton, RemoveButton, SaveButton, SettingsButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import NumberUtil from "@plannel/utils/number-util";
import colorUtil from "../utils/color-util";
import codeUtil from "@plannel/pages/utils/code-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import { grey, red } from "@mui/material/colors";

const { snackBarMessageColor } = colorUtil;
const CustomerMaster = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const customerSearch = useRef(reduxViewState?.searchCode ? reduxViewState.searchCode : "");
  const extraCustomerCodesRef = useRef(reduxViewState?.extraCustomerCodes ?? []);

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.page : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);
  const [sortParams, setSortParams] = useState({});

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [customerHierarchies, setCustomerHierarchies] = useState({});

  const hrchyNum = codeUtil.hrchyNum;

  const [attrConfigModalOpen, setAttrConfigModalOpen] = useState(false);
  const [attrColDefs, setAttrColDefs] = useState([]);
  const [hrchyColDefs, setHrchyColDefs] = useState([]);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");
  const [planConfig, setPlanConfig] = useState({});

  // disaggRatio 검증은 페이지를 넘는 형제까지 합산해야 하므로, 계층별 전체 고객을 미리 받아둔다.
  const siblingsByHrchyRef = useRef({});   // hrchyCd -> [{ customerCd, disaggRatio }] (삭제행 제외)
  const ratioErrorCdsRef = useRef(new Set()); // 현재 페이지에서 하이라이트할 customerCd

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const attrFields = [
    "attr01", "attr02", "attr03", "attr04", "attr05", "attr06", "attr07", "attr08", "attr09", "attr10",
    "attr11", "attr12", "attr13", "attr14", "attr15", "attr16", "attr17", "attr18", "attr19", "attr20"
  ];
  const attrColsCollapsed = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "attrColsCollapsed") ? reduxViewState.attrColsCollapsed : true;
  const hrchyNumList = {
    customerHrchy: hrchyNum.HRCHY,
    customerHrchy02: hrchyNum.HRCHY02,
    customerHrchy03: hrchyNum.HRCHY03,
    customerHrchy04: hrchyNum.HRCHY04,
    customerHrchy05: hrchyNum.HRCHY05
  }

  const getRowStyle = params => {
    const { delFlg } = params.data;
    if (delFlg) {
      return { background: grey[300] };
    }
    return DataState.getRowStyles(params);
  }

  const retrievePlanConfig = () => {
    const param = {
      moduleCd: codeUtil.module.DP,
      configCd: "CUST_RATIO_RANGE",
      activeFlg: true,
    };

    planConfigService.getConfigCd(param)
      .then(response => {
        if (response.data) {
          setPlanConfig(response.data);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (extraCustomerCodesRef.current?.length > 0) {
      searchFilters.customers = [customerSearch.current, ...extraCustomerCodesRef.current].filter(Boolean);
    } else if (customerSearch.current) {
      searchFilters.customer = customerSearch.current;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveCustomers = () => {
    const params = getRequestParams();
    customerService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setCustomers(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { searchCode: customerSearch.current, extraCustomerCodes: extraCustomerCodesRef.current, page: currentPage, pageSize, advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      })
  }

  const splitListByHrchyNum = (list) => {
    const result = {};
    list.forEach(item => {
      const num = item.hrchyNum;
      if (!Object.prototype.hasOwnProperty.call(result, num)) {
        result[num] = [];
      }
      result[num].push(item);
    });
    return result;
  }

  const retrieveCustomerHierarchies = () => {
    customerHrchyService.getLookup({})
      .then(response => {
        const result = response.data.results;
        setCustomerHierarchies(splitListByHrchyNum(result));
      })
      .catch(e => {
        debug(e);
      })
  }

  const refreshGrid = () => {
    retrieveCustomers();
  }

  const onGridReady = () => {
    retrieveCustomerHierarchies();
    setGridLoading(true);
  }

  const removeCustomer = () => {
    const customerIds = [];

    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        customerIds.push(data.id)
      }
    );

    if (customerIds.length > 0) {
      customerService.remove(customerIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          refreshGrid();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const hasRatioRange = () => {
    const objVal = planConfig?.objVal;
    return objVal != null && (objVal.min != null || objVal.max != null);
  }

  // 현재 페이지에 등장하는 계층들의 전체 고객(모든 페이지)을 받아 계층별로 캐싱한다.
  const fetchSiblingRatios = () => {
    if (!hasRatioRange() || customers === null || customers.length === 0) {
      siblingsByHrchyRef.current = {};
      ratioErrorCdsRef.current = new Set();
      return;
    }

    const custHrchyCds = [...new Set(customers.map(c => c.customerHrchy?.hrchyCd).filter(Boolean))];
    if (custHrchyCds.length === 0) {
      siblingsByHrchyRef.current = {};
      ratioErrorCdsRef.current = new Set();
      return;
    }

    customerService.getByHrchyCd({ custHrchyCds })
      .then(response => {
        const results = response?.data?.results ?? [];
        const grouped = {};
        for (const customer of results) {
          if (customer?.delFlg) {
            continue;
          }
          const key = customer.customerHrchy?.hrchyCd;
          if (!key) {
            continue;
          }
          if (!grouped[key]) {
            grouped[key] = [];
          }
          grouped[key].push({ customerCd: customer.customerCd, disaggRatio: customer.disaggRatio });
        }
        siblingsByHrchyRef.current = grouped;
        recomputeRatioErrors();
        gridRef.current?.api?.refreshCells({ columns: ["disaggRatio"], force: true });
      })
      .catch(e => {
        debug(e);
      });
  }

  // 페이지를 넘는 형제 전체 + 현재 페이지의 편집값을 합산해 범위를 벗어난 계층의 (현재 페이지) 고객을 표시한다.
  const recomputeRatioErrors = () => {
    const error = new Set();
    if (!hasRatioRange() || customers === null || customers.length === 0) {
      ratioErrorCdsRef.current = error;
      return;
    }

    const { min, max } = planConfig.objVal;

    // 현재 페이지 고객을 계층별로 그룹핑
    const pageByHrchy = customers.reduce((acc, customer) => {
      const key = customer.customerHrchy?.hrchyCd;
      if (!key) {
        return acc;
      }
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(customer);
      return acc;
    }, {});

    for (const hrchyCd of Object.keys(pageByHrchy)) {
      const pageChildren = pageByHrchy[hrchyCd];

      // 다른 페이지 형제(캐시) 위에 현재 페이지 값(편집/추가 반영)을 덮어써서 customerCd 기준 합집합을 만든다.
      const ratioByCd = new Map();
      for (const sibling of (siblingsByHrchyRef.current[hrchyCd] ?? [])) {
        ratioByCd.set(sibling.customerCd, sibling.disaggRatio);
      }
      for (const child of pageChildren) {
        if (child.delFlg) {
          ratioByCd.delete(child.customerCd);
          continue;
        }
        ratioByCd.set(child.customerCd, child.disaggRatio);
      }

      let totalDisaggRatio = null;
      for (const ratio of ratioByCd.values()) {
        if (ratio === null || ratio === undefined) {
          continue;
        }
        totalDisaggRatio = NumberUtil.decimalAdd(totalDisaggRatio, (ratio * 100 || 0));
      }

      if (totalDisaggRatio !== null && (totalDisaggRatio > max || totalDisaggRatio < min)) {
        for (const child of pageChildren) {
          error.add(child.customerCd);
        }
      }
    }

    ratioErrorCdsRef.current = error;
  }

  const saveCustomer = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length > 0) {
      const updatedOrigin = DataState.getUpdateOrigin(gridRef.current.api) || {};

      const dataWithFlg = allStateData.map((row) => {
        const originalData = updatedOrigin[row.rowIndex];
        let hrchyChanged = false;
        let activeFlgChanged = false;
        let markedAsDelete = false;
        if (originalData) {
          hrchyChanged = originalData?.customerHrchy?.id !== row?.customerHrchy?.id;
          activeFlgChanged = originalData?.activeFlg !== row?.activeFlg;
          markedAsDelete = originalData?.delFlg !== row?.delFlg && row?.delFlg === true;
        }
        const hrchyKeys = ["customerHrchy", "customerHrchy02", "customerHrchy03", "customerHrchy04", "customerHrchy05"];
        hrchyKeys.forEach(key => {
          if (row?.[key] === "") {
            row[key] = null;
          }
        });

        return {
          ...row,
          hrchyChanged,
          activeFlgChanged,
          markedAsDelete
        };
      });
      customerService.upsert(dataWithFlg)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          refreshGrid();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const checkDeleteFlgChanges = () => {
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    const updatedOrigin = DataState.getUpdateOrigin(gridRef.current.api) || {};

    return allStateData.some((row) => {
      const originalData = updatedOrigin[row.rowIndex];
      if (originalData) {
        return originalData.delFlg !== row.delFlg && row.delFlg === true;
      }
      return false;
    });
  };

  const checkActiveFlgChanges = () => {
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    const updatedOrigin = DataState.getUpdateOrigin(gridRef.current.api) || {};

    return allStateData.some((row) => {
      const originalData = updatedOrigin[row.rowIndex];
      if (originalData) {
        return originalData.activeFlg !== row.activeFlg && row.activeFlg === false;
      }
      return false;
    });
  };

  const handleSaveClick = () => {
    gridRef.current?.api?.stopEditing();

    const hasDeleteFlgChanges = checkDeleteFlgChanges();
    const hasActiveFlgChanges = checkActiveFlgChanges();

    if (hasDeleteFlgChanges) {
      setDialogInfo({ open: true, title: "delete", content: `${t("MSG_DeleteDataAssociated")}\n\n${codeUtil.makeMessage(codeUtil.deleteRelations.customer, 'delete')}` });
    } else if (hasActiveFlgChanges) {
      setDialogInfo({ open: true, title: "save", content: `${t("MSG_ActiveStatusChangedAssociated")}\n\n${codeUtil.makeMessage(codeUtil.deleteRelations.customer, 'deactivate')}` });
    } else {
      setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") });
    }
  };


  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveCustomer();
    } else if (action === "DELETE") {
      removeCustomer();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveCustomers();
    }
  }

  const addRow = () => {
    const newRow = {
      id: null,
      customerCd: "",
      name: "",
      customerHrchy: null,
      customerHrchy02: null,
      customerHrchy03: null,
      customerHrchy04: null,
      customerHrchy05: null,
      currencyCd: null,
      disaggRatio: null,
      attr01: null,
      attr02: null,
      attr03: null,
      attr04: null,
      attr05: null,
      attr06: null,
      attr07: null,
      attr08: null,
      attr09: null,
      attr10: null,
      activeFlg: true,
      priority: 999,
      delFlg: false,
      verNum: 0
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const hrchyValueFormatter = (customerHrchy) => {
    return customerHrchy.value ? customerHrchy.value.name : "";
  };

  const hrchyKeyCreator = (params) => {
    return params.value.hrchyCd;
  };

  const getCustHrchyList = (num) => {
    if (customerHierarchies === null) {
      return [];
    }
    if (Object.prototype.hasOwnProperty.call(customerHierarchies, num)) {
      return customerHierarchies[num];
    }
    return [];
  };

  const numberSetter = (params) => {
    if (params?.newValue === "" || params?.newValue === null) {
      params.data.disaggRatio = null;
      return true;
    }

    const newValue = parseFloat(params.newValue);
    if (isNaN(newValue) || newValue < 0) {
      return false;
    }

    if (params.data?.disaggRatio !== newValue) {
      params.data.disaggRatio = newValue;
      return true;
    }

    return false;
  }

  const exceptionCellStyle = params => {
    if (ratioErrorCdsRef.current.has(params.data.customerCd)) {
      return { color: red[700], fontWeight: "bold" };
    }

    return {};
  }

  // 편집 시 편집값을 반영해 계층 합계를 다시 계산하고 하이라이트를 갱신한다. (기본 편집 추적은 그대로 유지)
  const onCellValueChanged = (e) => {
    defaultGridMemo.onCellValueChanged?.(e);
    if (e?.colDef?.field === "disaggRatio") {
      recomputeRatioErrors();
      gridRef.current?.api?.refreshCells({ columns: ["disaggRatio"], force: true });
    }
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  const columnDefs = [
    {
      headerName: "customerCd",
      field: "customerCd",
      cellClass: 'stringType',
      filterType: 'string'
    },
    { headerName: "customerName", field: "name", filterType: 'string' },
    { headerName: "descTxt", field: "descTxt", filterType: 'string' },
    ...hrchyColDefs,
    { headerName: "currencyCd", field: "currencyCd", filterType: 'string' },
    { headerName: "disaggRatio", field: "disaggRatio", valueSetter: (params) => numberSetter(params), type: ["numberColumn", "percentColumn", "rightAligned"], width: 100, cellStyle: exceptionCellStyle, filterType: "percent" },
    ...attrColDefs,
    { headerName: "priority", field: "priority", type: ["rightAligned"], width: 100, filterType: 'number' },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], width: 80, filterType: 'boolean' },
    { headerName: "delFlg", field: "delFlg", type: ["booleanColumn"], width: 80, filterType: 'boolean' },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: "string" },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: "string" }
  ];

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveCustomers();
    }

    return () => {
      setCustomers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (Object.keys(hrchyColDefs).length > 0 && !isEmpty(attrColDefs)) {
      for (const row of hrchyColDefs) {
        row.cellEditorParams = {
          valueFormatter: hrchyValueFormatter,
          values: getCustHrchyList(hrchyNumList[row.field])
        }
        row.filterType = "string";
        row.filterOption = getCustHrchyList(hrchyNumList[row.field])
          .map(item => item.name) ?? null
            .filter((v, i, a) => v && a.indexOf(v) === i);
      }

      for (const row of attrColDefs) {
        row.filterType = "string";
        row.filterName = row.field;
      }

      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName, initState: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hrchyColDefs, attrColDefs, customerHierarchies]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }

    return () => {
      setCustomers([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  useEffect(() => {
    if (customers) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planConfig, customers]);

  useEffect(() => {
    fetchSiblingRatios();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, planConfig]);

  useEffect(() => {
    retrievePlanConfig();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FilterContainer>
        <CustomerAutocomplete retrieve={initPageToRetrieve} searchValue={customerSearch} useAccessFilter={false} extraCodesRef={extraCustomerCodesRef} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={customerService} params={getRequestParams()} columnDefs={columnDefs} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => {
            gridRef.current?.api?.stopEditing();
            setDialogInfo({ open: true, title: "delete", content: `${t("MSG_DeleteDataAssociated")}\n\n${codeUtil.makeMessage(codeUtil.deleteRelations.customer, 'delete')}` })
          }} />
          <SaveButton onClick={handleSaveClick} />
          <SettingsButton label="settings" onClick={() => setAttrConfigModalOpen(true)} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={customers}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              getRowStyle={getRowStyle}
              onGridReady={onGridReady}
              onCellValueChanged={onCellValueChanged}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <AttributeConfigModal
        viewName={viewName}
        open={attrConfigModalOpen}
        setOpen={setAttrConfigModalOpen}
        target={"customer"}
        attrFields={attrFields}
        hrchyColDefs={hrchyColDefs}
        hrchyColumnOptions={{
          type: ["defaultValueParser"],
          keyCreator: hrchyKeyCreator
        }}
        hrchyNumList={hrchyNumList}
        setAttrColDefs={setAttrColDefs}
        setHrchyColDefs={setHrchyColDefs}
        attrColsCollapsed={attrColsCollapsed}
        setPlanConfig={setPlanConfig}
        planConfig={planConfig}
        hasRatioRange
      />
    </>
  );
};

export default withTranslation()(CustomerMaster);